import React, { useState } from 'react';
import PropTypes from 'prop-types';

const fill = (color) => ({ fill: color, stroke: 'none' });

const stripes = (colors, vertical) => colors.map((color, i) => {
  const size = (vertical ? 24 : 16) / colors.length;

  return vertical
    ? <rect key={color} x={i * size} width={size} height="16" style={fill(color)} />
    : <rect key={color} y={i * size} width="24" height={size} style={fill(color)} />;
});

const FLAGS = {
  UA: stripes(['#0057b7', '#ffd700']),
  IT: stripes(['#009246', '#ffffff', '#ce2b37'], true),
  FR: stripes(['#0055a4', '#ffffff', '#ef4135'], true),
  DE: stripes(['#000000', '#dd0000', '#ffce00']),
  PL: stripes(['#ffffff', '#dc143c']),
  ES: [
    <rect key="r" width="24" height="16" style={fill('#aa151b')} />,
    <rect key="y" y="4" width="24" height="8" style={fill('#f1bf00')} />,
  ],
  EN: [
    <rect key="b" width="24" height="16" style={fill('#012169')} />,
    <path key="wd" d="M0 0 24 16M24 0 0 16" style={{ fill: 'none', stroke: '#ffffff', strokeWidth: 3.2 }} />,
    <path key="rd" d="M0 0 24 16M24 0 0 16" style={{ fill: 'none', stroke: '#c8102e', strokeWidth: 1.2 }} />,
    <path key="wc" d="M12 0v16M0 8h24" style={{ fill: 'none', stroke: '#ffffff', strokeWidth: 5 }} />,
    <path key="rc" d="M12 0v16M0 8h24" style={{ fill: 'none', stroke: '#c8102e', strokeWidth: 3 }} />,
  ],
  TR: [
    <rect key="r" width="24" height="16" style={fill('#e30a17')} />,
    <circle key="w" cx="9.5" cy="8" r="4" style={fill('#ffffff')} />,
    <circle key="c" cx="10.6" cy="8" r="3.2" style={fill('#e30a17')} />,
    <polygon key="s" points="14.6,8 16.9,7.2 15.5,9.2 15.5,6.8 16.9,8.8" style={fill('#ffffff')} />,
  ],
  ZH: [
    <rect key="r" width="24" height="16" style={fill('#de2910')} />,
    <polygon key="s" points="5,1.6 6,4.6 9.1,4.6 6.6,6.4 7.5,9.4 5,7.6 2.5,9.4 3.4,6.4 0.9,4.6 4,4.6" style={fill('#ffde00')} />,
  ],
  JA: [
    <rect key="w" width="24" height="16" style={fill('#ffffff')} />,
    <circle key="c" cx="12" cy="8" r="4.6" style={fill('#bc002d')} />,
  ],
  KO: [
    <rect key="w" width="24" height="16" style={fill('#ffffff')} />,
    <path key="t" d="M8 8a4 4 0 0 1 8 0z" style={fill('#cd2e3a')} />,
    <path key="b" d="M8 8a4 4 0 0 0 8 0z" style={fill('#0047a0')} />,
  ],
};

const SvgFlag = ({ code }) => (
  <svg className="hw-dir__flag" viewBox="0 0 24 16" aria-hidden="true">{FLAGS[code]}</svg>
);

SvgFlag.propTypes = {
  code: PropTypes.string.isRequired,
};

const ForeignFlag = ({ language }) => {
  const [broken, setBroken] = useState(false);

  if (language.image && !broken) {
    return (
      <img
        className="hw-dir__flag"
        src={language.image}
        alt=""
        width="24"
        height="16"
        decoding="async"
        onError={() => setBroken(true)}
      />
    );
  }

  if (FLAGS[language.code]) {
    return <SvgFlag code={language.code} />;
  }

  return <span className="hw-dir__code">{language.code}</span>;
};

ForeignFlag.propTypes = {
  language: PropTypes.shape({
    code: PropTypes.string,
    image: PropTypes.string,
  }).isRequired,
};

const Arrow = () => (
  <svg className="hw-dir__arrow" viewBox="0 0 48 26" aria-hidden="true">
    <circle cx="6" cy="13" r="3" style={{ fill: '#6b298a', fillOpacity: 0.4, stroke: 'none' }} />
    <circle cx="15" cy="13" r="3.4" style={{ fill: '#6b298a', fillOpacity: 0.7, stroke: 'none' }} />
    <path
      d="M24 4.5 43 13 24 21.5 28 13Z"
      style={{ fill: '#6b298a', stroke: '#6b298a', strokeWidth: 3, strokeLinejoin: 'round' }}
    />
  </svg>
);

const HomeworkDirection = ({ direction, language }) => {
  if (!language || !['to_ua', 'to_foreign'].includes(direction)) return null;

  const foreignName = language.name || language.code;
  const toUa = direction === 'to_ua';
  const label = toUa ? `${foreignName} → українська` : `Українська → ${foreignName}`;
  const ua = <SvgFlag code="UA" />;
  const foreign = <ForeignFlag language={language} />;

  return (
    <span className="hw-dir" role="img" aria-label={label} title={label}>
      {toUa ? foreign : ua}
      <Arrow />
      {toUa ? ua : foreign}
    </span>
  );
};

HomeworkDirection.propTypes = {
  direction: PropTypes.string,
  language: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    image: PropTypes.string,
  }),
};

export default HomeworkDirection;
